import { useTranslation } from "react-i18next"

import type { StyleTemplate } from "@/features/studio-ai/types/studio-ai.types"

import { MoreHorizontal, Pencil, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

type StyleTemplateCardProps = {
  template: StyleTemplate
  onEdit?: (template: StyleTemplate) => void
  onDelete?: (template: StyleTemplate) => void
  onSelect?: (template: StyleTemplate) => void
}

export function StyleTemplateCard({
  template,
  onEdit,
  onDelete,
  onSelect,
}: StyleTemplateCardProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })

  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
      role="presentation"
      onClick={() => onSelect?.(template)}
    >
      {/* Preview image */}
      <div className="bg-muted relative aspect-video">
        {template.preview_image_url ? (
          <img
            alt={template.title}
            className="h-full w-full object-cover"
            src={template.preview_image_url}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            {/* Color palette preview */}
            <div className="flex gap-1">
              {template.brand_colors.length > 0 ? (
                template.brand_colors.map((color, i) => (
                  <div
                    key={i}
                    className="h-10 w-10 rounded-lg shadow-sm"
                    style={{ backgroundColor: color }}
                  />
                ))
              ) : (
                <div className="text-muted-foreground text-sm">{t("noTemplates")}</div>
              )}
            </div>
          </div>
        )}

        {/* Actions menu */}
        {(onEdit || onDelete) && (
          <div className="absolute top-2 right-2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="h-8 w-8"
                  size="icon"
                  variant="secondary"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      onEdit(template)
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("edit")}
                  </DropdownMenuItem>
                )}
                {onDelete && !template.is_default && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete(template)
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <CardContent className="p-3">
        {onSelect ? (
          <button
            className="focus-visible:ring-ring mb-1 block max-w-full rounded-sm text-left text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(template)
            }}
          >
            {template.title}
          </button>
        ) : (
          <p className="mb-1 text-sm font-medium">{template.title}</p>
        )}
        <p className="text-muted-foreground mb-2 line-clamp-1 text-xs">{template.description}</p>
        <div className="flex gap-1.5">
          <Badge className="text-xs" variant="outline">
            {template.visibility === "company" ? t("company") : t("personal")}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}
